/**
 * RefundPolicy — public-facing refund / auto-renewal / cancellation page.
 *
 * Mirrors Section 12 of the signed waiver (lib/waiver.js v2.1+) in plain
 * language. The waiver itself remains the binding contract; this page
 * exists so:
 *   - Customers can read the policy anytime without digging through the
 *     waiver PDF.
 *   - Stripe disputes can be answered with a publicly accessible URL.
 *   - The waiver explicitly incorporates this page by reference, so
 *     either document covers the other.
 *
 * Sachin spec 2026-02-28.
 */
import { Link } from "react-router-dom";
import { Scale, AlertCircle, FileText } from "lucide-react";

const LAST_UPDATED = "February 28, 2026";

export default function RefundPolicy() {
    return (
        <div className="min-h-screen bg-brand-obsidian text-white pt-24 pb-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative max-w-3xl mx-auto px-6">
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                    <Scale className="w-3 h-3" />
                    Legal
                </div>
                <h1 data-testid="refund-policy-title" className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                    REFUND &amp;<br />
                    <span className="text-brand-red">CANCELLATION POLICY</span>
                </h1>
                <p className="text-zinc-400 text-sm mt-4">
                    Last updated · {LAST_UPDATED} · Version {/* keep in sync with WAIVER_VERSION */}v2.1-WA
                </p>

                <div className="mt-8 border border-amber-700/40 bg-amber-900/10 rounded-lg p-4 flex items-start gap-3 text-sm text-amber-100">
                    <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="leading-relaxed">
                        This page is the plain-language version of <strong>Section 12</strong> of the
                        MaxSC Participant Agreement (the waiver). The signed waiver is the binding
                        contract; this page is provided for easy reference and is{" "}
                        <strong>incorporated into the waiver by reference</strong>. If anything here
                        conflicts with the signed waiver, the waiver controls.
                    </div>
                </div>

                <Section title="1. Subscriptions are recurring and auto-renew">
                    <p>
                        All MaxSC memberships are sold as <strong>recurring subscriptions</strong>{" "}
                        charged in advance, on the cadence stated at sign-up (weekly unless
                        otherwise specified). You authorize MaxSC to charge the payment method on
                        file each billing period until you cancel.
                    </p>
                </Section>

                <Section title="2. Initial commitment">
                    <p>
                        Memberships have a committed term you chose at sign-up (commonly 3, 6, or
                        12 weeks). During the committed term, the subscription{" "}
                        <strong>cannot be cancelled</strong> and no refunds are owed for unused
                        sessions, missed classes, or schedule changes.
                    </p>
                </Section>

                <Section title="3. What happens when your commitment ends">
                    <p>
                        After the committed term, your subscription{" "}
                        <strong>automatically rolls into an open-ended weekly membership</strong>
                        {" "}at the then-current weekly rate, unless you cancel before the next
                        billing date.
                    </p>
                    <p>
                        We send a <strong>courtesy reminder email approximately 7 days</strong>{" "}
                        before your commitment ends so you have time to make a decision. You&apos;re
                        responsible for cancelling on time even if that reminder doesn&apos;t reach you
                        (spam folder, email change, etc.).
                    </p>
                </Section>

                <Section title="4. How to cancel — the 48-hour rule">
                    <p>
                        To cancel a subscription <strong>before</strong> the next charge, you must
                        submit the cancellation through your account&apos;s{" "}
                        <strong>&ldquo;Cancel Subscription&rdquo;</strong> control{" "}
                        <strong>at least 48 hours before</strong> the next scheduled billing date.
                    </p>
                    <p>
                        Cancellations submitted <em>within</em> 48 hours of the next charge will
                        take effect <strong>after</strong> that charge — the upcoming charge is
                        non-refundable, and your membership will end at the billing period
                        following it.
                    </p>
                </Section>

                <Section title="5. Refunds — when they happen and when they don't">
                    <p>
                        Once a billing period has begun, the charge for that period is{" "}
                        <strong>fully earned by MaxSC and is non-refundable</strong> under any
                        circumstance, including (but not limited to):
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-zinc-300 mt-2 ml-1">
                        <li>Lack of attendance</li>
                        <li>Change of mind</li>
                        <li>Schedule conflict, school activities, travel, relocation</li>
                        <li>Illness, injury, weather</li>
                    </ul>
                    <p className="mt-2">
                        The only exception is where a refund is required by Washington state law.
                    </p>
                </Section>

                <Section title="6. Session packs">
                    <p>
                        Session packs purchased outside of a subscription are{" "}
                        <strong>non-refundable</strong> once <em>any</em> session in the pack has
                        been redeemed. Unused sessions in a pack expire on the expiration date
                        printed at purchase.
                    </p>
                    <p>
                        Expired sessions <strong>may be reactivated</strong>, at MaxSC&apos;s option,
                        for a small administrative per-session fee (your coach shares the current
                        rate when you ask).
                    </p>
                </Section>

                <Section title="7. No-shows and late cancellations">
                    <p>
                        Sessions you no-show, late-cancel, or miss for any reason are{" "}
                        <strong>forfeited</strong> and are <strong>neither refundable nor
                        creditable</strong>, regardless of cause. The MaxSC strike system
                        documented separately in your account governs the operational consequences.
                    </p>
                </Section>

                <Section title="8. Chargebacks">
                    <p>
                        If you have a billing concern, please use the in-app{" "}
                        <strong>Cancel Subscription</strong> control or contact us first. Filing
                        a chargeback with your card issuer instead of using the in-app
                        cancellation control is a material breach of the Participant Agreement
                        and may result in:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-zinc-300 mt-2 ml-1">
                        <li>Immediate suspension of services</li>
                        <li>
                            Recovery of disputed amounts plus a reasonable administrative fee
                        </li>
                        <li>Reporting to collection agencies</li>
                    </ul>
                </Section>

                <Section title="9. Questions">
                    <p>
                        We want every billing decision to feel fair — if anything&apos;s unclear,
                        email <a href="mailto:sachinm@maxsc.net" className="text-brand-red hover:underline">sachinm@maxsc.net</a> or call <a href="tel:+14256414310" className="text-brand-red hover:underline">(425) 641-4310</a>. We&apos;ll work with you.
                    </p>
                </Section>

                <div className="mt-12 border-t border-zinc-900 pt-6 flex flex-wrap items-center gap-4 text-sm">
                    <Link
                        to="/waiver"
                        className="inline-flex items-center gap-2 text-zinc-300 hover:text-white transition-colors"
                    >
                        <FileText className="w-4 h-4" />
                        Read the full Participant Agreement (waiver) →
                    </Link>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="mt-9">
            <h2 className="font-display text-[18px] tracking-[0.1em] uppercase text-white mb-3">
                {title}
            </h2>
            <div className="text-sm text-zinc-300 leading-relaxed space-y-3">{children}</div>
        </section>
    );
}
